import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { 
  ChevronDownIcon,
  ChevronUpIcon,
  FilterIcon,
  LocationMarkerIcon,
  CurrencyDollarIcon,
  DesktopComputerIcon,
  CheckCircleIcon,
  XCircleIcon,
  SwitchHorizontalIcon,
  AdjustmentsIcon,
  XIcon,
  SearchIcon,
  MinusIcon,
  PlusIcon,
  CheckIcon,
  SelectorIcon,
  ViewGridIcon,
  ViewListIcon,
  TagIcon,
  HomeIcon,
  OfficeBuildingIcon,
  ShoppingBagIcon,
  AcademicCapIcon
} from '@heroicons/react/outline';

// Types de sites disponibles
const siteTypes = [
  { id: 'supermarket', name: 'Supermarchés', icon: <ShoppingBagIcon className="h-4 w-4" />, count: 24 },
  { id: 'mall', name: 'Centres commerciaux', icon: <ShoppingBagIcon className="h-4 w-4" />, count: 18 },
  { id: 'restaurant', name: 'Restaurants', icon: <HomeIcon className="h-4 w-4" />, count: 15 },
  { id: 'hotel', name: 'Hôtels', icon: <HomeIcon className="h-4 w-4" />, count: 12 },
  { id: 'office', name: 'Immeubles de bureaux', icon: <OfficeBuildingIcon className="h-4 w-4" />, count: 22 },
  { id: 'university', name: 'Universités', icon: <AcademicCapIcon className="h-4 w-4" />, count: 10 },
  { id: 'public', name: 'Espaces publics', icon: <LocationMarkerIcon className="h-4 w-4" />, count: 14 }
];

// Villes disponibles
const cities = [
  { id: 'douala', name: 'Douala', count: 52 },
  { id: 'yaounde', name: 'Yaoundé', count: 38 },
  { id: 'bafoussam', name: 'Bafoussam', count: 12 },
  { id: 'limbe', name: 'Limbé', count: 8 },
  { id: 'garoua', name: 'Garoua', count: 5 },
  { id: 'kribi', name: 'Kribi', count: 4 }
];

// Types de packages disponibles
const packageTypes = [
  { id: 'retail', name: 'Retail Network', icon: <ShoppingBagIcon className="h-4 w-4" />, count: 3 },
  { id: 'business', name: 'Business District', icon: <OfficeBuildingIcon className="h-4 w-4" />, count: 2 },
  { id: 'lifestyle', name: 'Lifestyle & Leisure', icon: <HomeIcon className="h-4 w-4" />, count: 4 },
  { id: 'education', name: 'Knowledge Network', icon: <AcademicCapIcon className="h-4 w-4" />, count: 2 }
];

const FilterSidebar = ({ onFilterChange, viewMode, setViewMode }) => {
  // États des filtres
  const [isFilterOpen, setIsFilterOpen] = useState(true); // Mobile: fermé par défaut, Desktop: ouvert
  const [activeView, setActiveView] = useState('sites'); // 'sites' ou 'packages'
  const [priceRange, setPriceRange] = useState([5000, 100000]); // Prix min et max
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedSiteTypes, setSelectedSiteTypes] = useState([]);
  const [selectedPackageTypes, setSelectedPackageTypes] = useState([]);
  const [displayMode, setDisplayMode] = useState('grid'); // 'grid' ou 'list'
  const [screenType, setScreenType] = useState('all'); // 'indoor', 'outdoor', 'all'
  const [availabilityFilter, setAvailabilityFilter] = useState('all'); // 'available', 'reserved', 'all'
  const [minScreens, setMinScreens] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    price: true,
    cities: true,
    siteType: true,
    screenType: true,
    screens: true,
    availability: true
  });

  // Références pour les interactions et animations
  const priceMinRef = useRef(null);
  const priceMaxRef = useRef(null);
  const sidebarRef = useRef(null);
  
  // Détecter si l'écran est mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Sur mobile, fermer le filtre par défaut
      if (window.innerWidth < 768) {
        setIsFilterOpen(false);
      }
    };
    
    // Vérifier à l'initialisation
    checkIfMobile();
    
    // Ajouter l'écouteur d'événement
    window.addEventListener('resize', checkIfMobile);
    
    // Nettoyer l'écouteur
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Mettre à jour les filtres parent lorsqu'un filtre change
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange({
        activeView,
        priceRange,
        selectedCities,
        selectedSiteTypes,
        selectedPackageTypes,
        screenType,
        availabilityFilter,
        minScreens,
        searchQuery,
        displayMode
      });
    }
  }, [
    activeView,
    priceRange, 
    selectedCities, 
    selectedSiteTypes,
    selectedPackageTypes,
    screenType,
    availabilityFilter,
    minScreens,
    searchQuery,
    displayMode,
    onFilterChange
  ]);

  // Gestionnaires d'événements pour les filtres
  const handleViewModeChange = (mode) => {
    setActiveView(mode);
    // Réinitialiser certains filtres lors du changement de mode
    if (mode === 'sites') {
      setSelectedPackageTypes([]);
    } else {
      setSelectedSiteTypes([]);
      setScreenType('all');
      setMinScreens(1);
    }
  };

  const handlePriceChange = (minOrMax, value) => {
    const newValue = parseInt(value, 10) || 0;
    
    if (minOrMax === 'min') {
      // Assurer que min ne dépasse pas max
      setPriceRange([Math.min(newValue, priceRange[1]), priceRange[1]]);
    } else {
      // Assurer que max n'est pas inférieur à min
      setPriceRange([priceRange[0], Math.max(newValue, priceRange[0])]);
    }
  };

  const handleCityToggle = (cityId) => {
    setSelectedCities(prev => {
      if (prev.includes(cityId)) {
        return prev.filter(id => id !== cityId);
      } else {
        return [...prev, cityId];
      }
    });
  };

  const handleSiteTypeToggle = (typeId) => {
    setSelectedSiteTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(id => id !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
  };

  const handlePackageTypeToggle = (typeId) => {
    setSelectedPackageTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(id => id !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
  };

  const handleScreenTypeChange = (type) => {
    setScreenType(type);
  };

  const handleAvailabilityChange = (status) => {
    setAvailabilityFilter(status);
  };

  const handleMinScreensChange = (value) => {
    const newValue = parseInt(value, 10) || 1;
    setMinScreens(Math.max(1, newValue));
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const resetFilters = () => {
    setPriceRange([5000, 1000000]);
    setSelectedCities([]);
    setSelectedSiteTypes([]);
    setSelectedPackageTypes([]);
    setScreenType('all');
    setAvailabilityFilter('all');
    setMinScreens(1);
    setSearchQuery('');
  };

  // Animations
  const sidebarVariants = {
    open: { 
      x: 0,
      width: isMobile ? "85%" : "auto",
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 30,
        duration: 0.3
      }
    },
    closed: { 
      x: isMobile ? "-100%" : 0,
      width: isMobile ? "85%" : "auto",
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 30,
        duration: 0.3
      }
    }
  };

  const filterItemVariants = {
    open: { opacity: 1, height: "auto" },
    closed: { opacity: 0, height: 0 }
  };

  return (
    <>
      {/* Bouton d'ouverture du filtre sur mobile */}
      {isMobile && (
        <motion.button
          className="fixed bottom-6 left-6 z-40 flex items-center space-x-2 bg-[#E84E0E] text-white px-4 py-3 rounded-full shadow-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsFilterOpen(true)}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <FilterIcon className="h-5 w-5" />
          <span className="font-medium">Filtres</span>
        </motion.button>
      )}
      
      {/* Overlay pour fermer le filtre sur mobile */}
      {isMobile && isFilterOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsFilterOpen(false)}
        />
      )}
      
      {/* Sidebar principale */}
      <motion.div
        ref={sidebarRef}
        className={`${
          isMobile 
            ? "fixed left-0 top-0 bottom-0 z-50 overflow-y-auto" 
            : "sticky top-24 h-[calc(100vh-120px)] overflow-y-auto"
        } bg-white shadow-xl rounded-r-2xl w-full max-w-xs`}
        variants={sidebarVariants}
        initial={isMobile ? "closed" : "open"}
        animate={isFilterOpen ? "open" : "closed"}
      >
        {/* Header avec titre et bouton fermer sur mobile */}
        <div className="border-b border-gray-200 p-4 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <FilterIcon className="h-5 w-5 text-[#E84E0E]" />
              <h2 className="font-bold text-lg text-gray-800">Filtres</h2>
            </div>
            
            {isMobile && (
              <button onClick={() => setIsFilterOpen(false)} className="p-1.5 rounded-full hover:bg-gray-100">
                <XIcon className="h-5 w-5 text-gray-500" />
              </button>
            )}
          </div>
          
          {/* Barre de recherche */}
          <div className="mt-4 relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full bg-gray-100 py-2.5 pl-10 pr-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E84E0E] text-gray-700"
            />
          </div>
        </div>
        
        {/* Contenu du filtre avec défilement */}
        <div className="p-4">
          {/* Basculement Sites/Packages */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
              <SwitchHorizontalIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
              <span>Type d'affichage</span>
            </h3>
            
            <div className="flex rounded-xl bg-gray-100 p-1">
              <button
                onClick={() => handleViewModeChange('sites')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium ${
                  activeView === 'sites' 
                    ? 'bg-white shadow-sm text-[#E84E0E]' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Sites individuels
              </button>
              <button
                onClick={() => handleViewModeChange('packages')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium ${
                  activeView === 'packages' 
                    ? 'bg-white shadow-sm text-[#E84E0E]' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Packages
              </button>
            </div>
          </div>
          
          {/* Style d'affichage (Grille/Liste) */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
              <AdjustmentsIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
              <span>Style d'affichage</span>
            </h3>
            
            <div className="flex rounded-xl bg-gray-100 p-1">
              <button
                onClick={() => setDisplayMode('grid')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium flex items-center justify-center ${
                  displayMode === 'grid' 
                    ? 'bg-white shadow-sm text-[#E84E0E]' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ViewGridIcon className="h-5 w-5 mr-1.5" />
                Grille
              </button>
              <button
                onClick={() => setDisplayMode('list')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium flex items-center justify-center ${
                  displayMode === 'list' 
                    ? 'bg-white shadow-sm text-[#E84E0E]' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ViewListIcon className="h-5 w-5 mr-1.5" />
                Liste
              </button>
            </div>
          </div>
          
          {/* Séparateur */}
          <div className="border-t border-gray-200 my-6"></div>
          
          {/* Section Prix - Toujours visible quel que soit le mode */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('price')}
              className="flex items-center justify-between w-full font-semibold text-gray-700 mb-3"
            >
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                <span>Fourchette de prix</span>
              </div>
              {expandedSections.price ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>
            
            <AnimatePresence>
              {expandedSections.price && (
                <motion.div
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={filterItemVariants}
                >
                  <div className="mb-3">
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-gray-600">Prix minimum:</label>
                      <span className="text-sm font-medium text-gray-800">{priceRange[0].toLocaleString()} FCFA</span>
                    </div>
                    <input
                      type="range"
                      min="5000"
                      max="100000"
                      step="1000"
                      value={priceRange[0]}
                      onChange={(e) => handlePriceChange('min', e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#E84E0E]"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-gray-600">Prix maximum:</label>
                      <span className="text-sm font-medium text-gray-800">{priceRange[1].toLocaleString()} FCFA</span>
                    </div>
                    <input
                      type="range"
                      min="5000"
                      max="100000"
                      step="1000"
                      value={priceRange[1]}
                      onChange={(e) => handlePriceChange('max', e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#E84E0E]"
                    />
                  </div>
                  
                  <div className="flex space-x-3 mt-4">
                    <div className="flex-1">
                      <input
                        type="number"
                        ref={priceMinRef}
                        value={priceRange[0]}
                        onChange={(e) => handlePriceChange('min', e.target.value)}
                        className="w-full p-2.5 border border-gray-300 rounded-lg"
                        placeholder="Min"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="number"
                        ref={priceMaxRef}
                        value={priceRange[1]}
                        onChange={(e) => handlePriceChange('max', e.target.value)}
                        className="w-full p-2.5 border border-gray-300 rounded-lg"
                        placeholder="Max"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Section Villes - Toujours visible quel que soit le mode */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('cities')}
              className="flex items-center justify-between w-full font-semibold text-gray-700 mb-3"
            >
              <div className="flex items-center">
                <LocationMarkerIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                <span>Villes</span>
              </div>
              {expandedSections.cities ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>
            
            <AnimatePresence>
              {expandedSections.cities && (
                <motion.div
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={filterItemVariants}
                  className="max-h-60 overflow-y-auto custom-scrollbar"
                >
                  {cities.map((city) => (
                    <div key={city.id} className="flex items-center mb-2">
                      <button
                        onClick={() => handleCityToggle(city.id)}
                        className={`w-full p-2.5 rounded-lg flex justify-between items-center ${
                          selectedCities.includes(city.id)
                            ? 'bg-orange-50 text-[#E84E0E] border border-orange-200'
                            : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
                        }`}
                      >
                        <span>{city.name}</span>
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 mr-2">{city.count}</span>
                          {selectedCities.includes(city.id) && (
                            <CheckIcon className="h-5 w-5 text-[#E84E0E]" />
                          )}
                        </div>
                      </button>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Filtres conditionnels en fonction du mode d'affichage */}
          {activeView === 'sites' ? (
            <>
              {/* Section Types de Sites - Mode Sites uniquement */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('siteType')}
                  className="flex items-center justify-between w-full font-semibold text-gray-700 mb-3"
                >
                  <div className="flex items-center">
                    <TagIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                    <span>Types de sites</span>
                  </div>
                  {expandedSections.siteType ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                <AnimatePresence>
                  {expandedSections.siteType && (
                    <motion.div
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={filterItemVariants}
                      className="max-h-60 overflow-y-auto custom-scrollbar"
                    >
                      {siteTypes.map((type) => (
                        <div key={type.id} className="flex items-center mb-2">
                          <button
                            onClick={() => handleSiteTypeToggle(type.id)}
                            className={`w-full p-2.5 rounded-lg flex justify-between items-center ${
                              selectedSiteTypes.includes(type.id)
                                ? 'bg-orange-50 text-[#E84E0E] border border-orange-200'
                                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center">
                              <span className="mr-2">{type.icon}</span>
                              <span>{type.name}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-sm text-gray-500 mr-2">{type.count}</span>
                              {selectedSiteTypes.includes(type.id) && (
                                <CheckIcon className="h-5 w-5 text-[#E84E0E]" />
                              )}
                            </div>
                          </button>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Section Type d'écran - Mode Sites uniquement */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('screenType')}
                  className="flex items-center justify-between w-full font-semibold text-gray-700 mb-3"
                >
                  <div className="flex items-center">
                    <DesktopComputerIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                    <span>Type d'écran</span>
                  </div>
                  {expandedSections.screenType ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                <AnimatePresence>
                  {expandedSections.screenType && (
                    <motion.div
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={filterItemVariants}
                    >
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleScreenTypeChange('all')}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                            screenType === 'all' 
                              ? 'bg-[#E84E0E] text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Tous
                        </button>
                        <button
                          onClick={() => handleScreenTypeChange('indoor')}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                            screenType === 'indoor' 
                              ? 'bg-[#E84E0E] text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Indoor
                        </button>
                        <button
                          onClick={() => handleScreenTypeChange('outdoor')}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                            screenType === 'outdoor' 
                              ? 'bg-[#E84E0E] text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Outdoor
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Section Nombre d'écrans - Mode Sites uniquement */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('screens')}
                  className="flex items-center justify-between w-full font-semibold text-gray-700 mb-3"
                >
                  <div className="flex items-center">
                    <DesktopComputerIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                    <span>Nombre d'écrans minimum</span>
                  </div>
                  {expandedSections.screens ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                <AnimatePresence>
                  {expandedSections.screens && (
                    <motion.div
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={filterItemVariants}
                    >
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleMinScreensChange(Math.max(1, minScreens - 1))}
                          className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700"
                        >
                          <MinusIcon className="h-5 w-5" />
                        </button>
                        
                        <input
                          type="number"
                          min="1"
                          value={minScreens}
                          onChange={(e) => handleMinScreensChange(e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded-lg text-center"
                        />
                        
                        <button
                          onClick={() => handleMinScreensChange(minScreens + 1)}
                          className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700"
                        >
                          <PlusIcon className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <div className="mt-3 text-center text-sm text-gray-600">
                        Sites avec {minScreens} écran{minScreens > 1 ? 's' : ''} ou plus
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            // Si en mode Packages
            <>
              {/* Section Types de Packages - Mode Packages uniquement */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('packageType')}
                  className="flex items-center justify-between w-full font-semibold text-gray-700 mb-3"
                >
                  <div className="flex items-center">
                    <TagIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                    <span>Types de packages</span>
                  </div>
                  {expandedSections.packageType ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                <AnimatePresence>
                  {expandedSections.packageType && (
                    <motion.div
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={filterItemVariants}
                      className="max-h-60 overflow-y-auto custom-scrollbar"
                    >
                      {packageTypes.map((type) => (
                        <div key={type.id} className="flex items-center mb-2">
                          <button
                            onClick={() => handlePackageTypeToggle(type.id)}
                            className={`w-full p-2.5 rounded-lg flex justify-between items-center ${
                              selectedPackageTypes.includes(type.id)
                                ? 'bg-orange-50 text-[#E84E0E] border border-orange-200'
                                : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center">
                              <span className="mr-2">{type.icon}</span>
                              <span>{type.name}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-sm text-gray-500 mr-2">{type.count}</span>
                              {selectedPackageTypes.includes(type.id) && (
                                <CheckIcon className="h-5 w-5 text-[#E84E0E]" />
                              )}
                            </div>
                          </button>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
          
          {/* Section Disponibilité - Toujours visible quel que soit le mode */}
          <div className="mb-6">
            <button
              onClick={() => toggleSection('availability')}
              className="flex items-center justify-between w-full font-semibold text-gray-700 mb-3"
            >
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                <span>Disponibilité</span>
              </div>
              {expandedSections.availability ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>
            
            <AnimatePresence>
              {expandedSections.availability && (
                <motion.div
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={filterItemVariants}
                >
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleAvailabilityChange('all')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                        availabilityFilter === 'all' 
                          ? 'bg-[#E84E0E] text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Tous
                    </button>
                    <button
                      onClick={() => handleAvailabilityChange('available')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                        availabilityFilter === 'available' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Disponibles
                    </button>
                    <button
                      onClick={() => handleAvailabilityChange('reserved')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                        availabilityFilter === 'reserved' 
                          ? 'bg-red-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Réservés
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Boutons d'action */}
          <div className="mt-8 space-y-3">
            <button
              onClick={resetFilters}
              className="w-full py-3 px-4 rounded-xl bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
            >
              <XIcon className="h-5 w-5 mr-2" />
              Réinitialiser les filtres
            </button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3 px-4 rounded-xl bg-[#E84E0E] text-white font-medium hover:bg-[#d04305] transition-colors flex items-center justify-center"
            >
              <FilterIcon className="h-5 w-5 mr-2" />
              Appliquer les filtres
            </motion.button>
          </div>
        </div>
      </motion.div>
      
      {/* Barre de filtres en version desktop (toujours visible) */}
      {!isMobile && (
        <div className="hidden md:flex items-center h-16 mb-6 bg-white rounded-xl shadow p-2 overflow-x-auto">
          {/* Affichage des filtres actifs sous forme de badges */}
          <div className="flex-1 flex items-center space-x-2 px-3 overflow-x-auto custom-scrollbar-horizontal">
            {activeView === 'sites' ? (
              <span className="bg-[#E84E0E] text-white px-3 py-1 rounded-full text-sm font-medium">
                Sites
              </span>
            ) : (
              <span className="bg-[#E84E0E] text-white px-3 py-1 rounded-full text-sm font-medium">
                Packages
              </span>
            )}
            
            {/* Prix */}
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center">
              <CurrencyDollarIcon className="h-4 w-4 mr-1" />
              {`${priceRange[0].toLocaleString()} - ${priceRange[1].toLocaleString()} FCFA`}
            </span>
            
            {/* Villes sélectionnées */}
            {selectedCities.length > 0 && (
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <LocationMarkerIcon className="h-4 w-4 mr-1" />
                {selectedCities.length === 1 
                  ? cities.find(c => c.id === selectedCities[0])?.name
                  : `${selectedCities.length} villes`
                }
              </span>
            )}
            
            {/* Types de sites sélectionnés */}
            {selectedSiteTypes.length > 0 && (
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <TagIcon className="h-4 w-4 mr-1" />
                {selectedSiteTypes.length === 1 
                  ? siteTypes.find(t => t.id === selectedSiteTypes[0])?.name
                  : `${selectedSiteTypes.length} types de sites`
                }
              </span>
            )}
            
            {/* Types de packages sélectionnés */}
            {selectedPackageTypes.length > 0 && (
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <TagIcon className="h-4 w-4 mr-1" />
                {selectedPackageTypes.length === 1 
                  ? packageTypes.find(t => t.id === selectedPackageTypes[0])?.name
                  : `${selectedPackageTypes.length} types de packages`
                }
              </span>
            )}
            
            {/* Type d'écran */}
            {screenType !== 'all' && (
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <DesktopComputerIcon className="h-4 w-4 mr-1" />
                {screenType === 'indoor' ? 'Indoor' : 'Outdoor'}
              </span>
            )}
            
            {/* Nombre minimum d'écrans */}
            {minScreens > 1 && (
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <DesktopComputerIcon className="h-4 w-4 mr-1" />
                {`Min. ${minScreens} écrans`}
              </span>
            )}
            
            {/* Disponibilité */}
            {availabilityFilter !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center ${
                availabilityFilter === 'available' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {availabilityFilter === 'available' 
                  ? <CheckCircleIcon className="h-4 w-4 mr-1" />
                  : <XCircleIcon className="h-4 w-4 mr-1" />
                }
                {availabilityFilter === 'available' ? 'Disponibles' : 'Réservés'}
              </span>
            )}
            
            {/* Recherche */}
            {searchQuery && (
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <SearchIcon className="h-4 w-4 mr-1" />
                {`"${searchQuery}"`}
              </span>
            )}
          </div>
          
          {/* Bouton pour ouvrir/fermer le filtre complet */}
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="ml-2 p-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 flex items-center justify-center"
          >
            {isFilterOpen ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      )}
      
      {/* Affichage du nombre de résultats et options de tri */}
      <div className="flex justify-between items-center mb-6 px-4 md:px-0">
        <div className="text-gray-700">
          <span className="font-medium">125</span> résultats trouvés
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-gray-600 text-sm hidden md:inline">Trier par:</span>
          <select className="bg-white border border-gray-200 rounded-lg py-2 pl-3 pr-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent appearance-none">
            <option>Popularité</option>
            <option>Prix croissant</option>
            <option>Prix décroissant</option>
            <option>Audience</option>
          </select>
        </div>
      </div>
      
      {/* Styles CSS personnalisés pour les scrollbars */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #ddd;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #ccc;
        }
        
        .custom-scrollbar-horizontal::-webkit-scrollbar {
          height: 4px;
        }
        
        .custom-scrollbar-horizontal::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar-horizontal::-webkit-scrollbar-thumb {
          background: #ddd;
          border-radius: 10px;
        }
        
        .custom-scrollbar-horizontal::-webkit-scrollbar-thumb:hover {
          background: #ccc;
        }
      `}</style>
    </>
  );
};

export default FilterSidebar;